import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Avatar } from '../avatar';

const AUTH_URL = 'https://dev-auth.apps.istor.io';

const getCookie = (name) => {
    const m = document.cookie.split('; ').find(r => r.startsWith(`${name}=`));
    return m ? m.split('=').slice(1).join('=') : undefined;
};

const normalize = (s) => (s || '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

export default function InviteModal({ open, onClose, onInvite, userAuth, membres }) {
    const [inviteInputFocused, setInviteInputFocused] = useState(false);
    const [inviteQuery, setInviteQuery] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [hoveredSearchItem, setHoveredSearchItem] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [hoveredCancelInvite, setHoveredCancelInvite] = useState(false);
    const [activeCancelInvite, setActiveCancelInvite] = useState(false);
    const [hoveredConfirmInvite, setHoveredConfirmInvite] = useState(false);
    const [activeConfirmInvite, setActiveConfirmInvite] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        if (!open) { setInviteQuery(''); setSelectedUsers([]); return; }
        const jwt = getCookie('jwt');
        if (!jwt) return;
        fetch(`${AUTH_URL}/user`, { headers: { 'Authorization': `Bearer ${jwt}` } })
            .then(r => r.ok ? r.json() : [])
            .then(setAllUsers)
            .catch(() => {});
    }, [open]);

    useLayoutEffect(() => {
        const el = modalRef.current;
        if (!el || !open) return;
        const from = el.getBoundingClientRect().height;
        el.style.transition = 'none';
        el.style.height = 'auto';
        const to = Math.min(el.scrollHeight, Math.floor(window.innerHeight * 0.8));
        el.style.height = from + 'px';
        void el.offsetHeight;
        el.style.transition = 'height 300ms ease';
        el.style.height = to + 'px';
    }, [selectedUsers, open]);

    const handleInvite = async () => {
        const jwt = getCookie('jwt');
        const orgId = userAuth?.organization?.id;
        if (!jwt || !orgId || selectedUsers.length === 0) return;
        const headers = { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' };
        // Un seul appel par invité : le back pose les rôles maximum sur tous les services
        // actifs de l'établissement, en une écriture atomique. Les trois appels concurrents
        // précédents (un par service) pouvaient se marcher dessus et laisser l'invité avec
        // des droits partiels.
        await Promise.all(selectedUsers.map(u =>
            fetch(`${AUTH_URL}/autorisation/organisation/${orgId}/membre`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ userId: u.id }),
            })
        ));
        onInvite(selectedUsers.map(u => ({ id: u.id, firstname: u.prenom, name: u.nom, avatarUrl: u.avatarUrl, owner: false })));
        onClose();
    };

    if (!open) return null;

    const memberIds = new Set([...(membres || []).map(m => m.id), ...selectedUsers.map(u => u.id)]);
    const q = normalize(inviteQuery.trim());
    const searchResults = q.length === 0 ? [] : allUsers.filter(u => {
        if (memberIds.has(u.id)) return false;
        return normalize(`${u.prenom} ${u.nom}`).includes(q)
            || normalize(`${u.nom} ${u.prenom}`).includes(q);
    });
    const showDropdown = inviteQuery.trim().length > 0;

    return createPortal(
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: '61px',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(64,91,106,0.8)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    zIndex: 2000,
                }}
            />
            {/* Modal */}
            <div
                ref={modalRef}
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '460px',
                    minHeight: '223px',
                    borderRadius: '20px',
                    backgroundColor: '#FFFFFF',
                    zIndex: 2001,
                    boxShadow: '0px 10px 40px rgba(0, 0, 0, 0.2)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible',
                }}
            >
                {/* Header */}
                <div style={{
                    width: '100%',
                    height: '60px',
                    flexShrink: 0,
                    backgroundColor: '#173C50',
                    borderRadius: '20px 20px 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '30px',
                }}>
                    <span style={{ fontWeight: 700, fontSize: '20px', color: '#FFFFFF', fontFamily: 'Lexend, system-ui, sans-serif' }}>Inviter un collaborateur</span>
                </div>

                {/* Corps */}
                <div style={{ flex: 1, padding: '20px 30px', display: 'flex', flexDirection: 'column' }}>
                    {/* Champ nom */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ fontWeight: 400, fontSize: '11px', color: '#505962', fontFamily: 'Lexend, system-ui, sans-serif' }}>
                            Entrez le nom de la personne que vous souhaitez inviter
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                value={inviteQuery}
                                onChange={e => setInviteQuery(e.target.value)}
                                onFocus={() => setInviteInputFocused(true)}
                                onBlur={() => setInviteInputFocused(false)}
                                style={{
                                    width: '100%',
                                    height: '36px',
                                    border: '1px solid #E0E0E0',
                                    borderRadius: '8px',
                                    padding: '0 12px',
                                    fontSize: '13px',
                                    fontFamily: 'Lexend, system-ui, sans-serif',
                                    color: '#173C50',
                                    backgroundColor: 'transparent',
                                    outline: 'none',
                                    boxShadow: inviteInputFocused ? '0 0 0 3px #8CB4D6' : '0 0 0 0px #8CB4D6',
                                    transition: 'box-shadow 150ms ease',
                                    boxSizing: 'border-box',
                                }}
                            />
                            {showDropdown && (
                                <div style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 5px)',
                                    left: 0,
                                    right: 0,
                                    borderRadius: '10px',
                                    border: '1px solid white',
                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                    backdropFilter: 'blur(50px)',
                                    WebkitBackdropFilter: 'blur(50px)',
                                    boxShadow: '0px 10px 40px rgba(0,0,0,0.2)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    padding: '5px',
                                    maxHeight: `${5 * 46 + 10}px`,
                                    overflowY: 'auto',
                                    zIndex: 10,
                                }}>
                                    {searchResults.length === 0 ? (
                                        <div style={{
                                            height: '55px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            paddingLeft: '16px',
                                            fontWeight: 600,
                                            fontSize: '12px',
                                            color: '#B0B0B0',
                                            fontFamily: 'Lexend, system-ui, sans-serif',
                                        }}>
                                            Il n'existe aucun utilisateur avec ce nom
                                        </div>
                                    ) : searchResults.map((u, i) => (
                                        <div
                                            key={u.id}
                                            onMouseEnter={() => setHoveredSearchItem(i)}
                                            onMouseLeave={() => setHoveredSearchItem(null)}
                                            onClick={() => { setSelectedUsers(prev => [...prev, u]); setInviteQuery(''); setHoveredSearchItem(null); }}
                                            style={{
                                                height: '46px',
                                                width: '100%',
                                                paddingLeft: '11px',
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: '10px',
                                                backgroundColor: hoveredSearchItem === i ? '#E9EDF2' : 'transparent',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                transition: 'background-color 100ms',
                                                boxSizing: 'border-box',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Avatar url={u.avatarUrl} nom={`${u.prenom ?? ''} ${u.nom ?? ''}`} size={36} />
                                            <span style={{ fontWeight: 600, fontSize: '12px', color: '#173C50', fontFamily: 'Lexend, system-ui, sans-serif' }}>
                                                {u.prenom} {u.nom}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Utilisateurs sélectionnés */}
                    {selectedUsers.length > 0 && (
                        <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '8px' }}>
                            {selectedUsers.map(u => (
                                <div key={u.id} style={{
                                    display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '6px',
                                    backgroundColor: '#F4F4F7', borderRadius: '20px',
                                    padding: '4px 10px 4px 6px',
                                }}>
                                    <Avatar url={u.avatarUrl} nom={`${u.prenom ?? ''} ${u.nom ?? ''}`} size={24} />
                                    <span style={{ fontWeight: 600, fontSize: '12px', color: '#173C50', fontFamily: 'Lexend, system-ui, sans-serif', whiteSpace: 'nowrap' }}>
                                        {u.prenom} {u.nom}
                                    </span>
                                    <button
                                        onClick={() => setSelectedUsers(prev => prev.filter(s => s.id !== u.id))}
                                        style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            width: '14px', height: '14px', borderRadius: '50%',
                                            backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0,
                                        }}
                                    >
                                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 1L9 9M9 1L1 9" stroke="#173C50" strokeWidth="1.5" strokeLinecap="round"/>
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Boutons */}
                    <div style={{ marginTop: selectedUsers.length > 0 ? '20px' : '30px', display: 'flex', flexDirection: 'row', gap: '10px', justifyContent: 'flex-end' }}>
                        <button
                            onClick={onClose}
                            onMouseEnter={() => setHoveredCancelInvite(true)}
                            onMouseLeave={() => { setHoveredCancelInvite(false); setActiveCancelInvite(false); }}
                            onMouseDown={() => setActiveCancelInvite(true)}
                            onMouseUp={() => setActiveCancelInvite(false)}
                            style={{
                                height: '36px',
                                borderRadius: '18px',
                                padding: '0 18px',
                                fontWeight: 600,
                                fontSize: '13px',
                                fontFamily: 'Lexend, system-ui, sans-serif',
                                color: '#F46969',
                                backgroundColor: activeCancelInvite ? 'rgba(255,113,113,0.3)' : hoveredCancelInvite ? 'rgba(255,202,202,0.3)' : 'transparent',
                                border: '1px solid #F46969',
                                cursor: 'pointer',
                                transition: 'background-color 100ms',
                            }}
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleInvite}
                            onMouseEnter={() => setHoveredConfirmInvite(true)}
                            onMouseLeave={() => { setHoveredConfirmInvite(false); setActiveConfirmInvite(false); }}
                            onMouseDown={() => setActiveConfirmInvite(true)}
                            onMouseUp={() => setActiveConfirmInvite(false)}
                            style={{
                                height: '36px',
                                borderRadius: '18px',
                                padding: '0 18px',
                                fontWeight: 600,
                                fontSize: '13px',
                                fontFamily: 'Lexend, system-ui, sans-serif',
                                color: '#FFFFFF',
                                backgroundColor: activeConfirmInvite ? '#206095' : hoveredConfirmInvite ? '#3779AE' : '#007BD3',
                                border: `1px solid ${activeConfirmInvite ? '#206095' : hoveredConfirmInvite ? '#3779AE' : '#007BD3'}`,
                                cursor: 'pointer',
                                transition: 'background-color 100ms, border-color 100ms',
                            }}
                        >
                            Inviter
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}
